import React, { useState, useEffect, useRef } from 'react';
import { SketchPicker } from 'react-color';
import PropTypes from 'prop-types';
import { getAuth } from 'firebase/auth';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    RadioGroup,
    FormControlLabel,
    Radio,
    TextField,
    Select,
    MenuItem,
    Box,
    Typography,
    FormControl,
    Divider,
    Popover
} from '@mui/material';
import HelpIcon from './HelpIcon';

const planets = ['Sun', 'Moon', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn', 'Uranus', 'Neptune', 'Pluto', 'Rahu', 'Ketu'];
const signs = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'];
const declinationAspects = ['Parallel', 'Parallel N', 'Parallel S', 'Contra-Parallel'];
const declinationAspectMap = {
    'Parallel': 'PARALLEL',
    'Parallel N': 'NPARALLEL',
    'Parallel S': 'SPARALLEL',
    'Contra-Parallel': 'CONTRAPARALLEL'
};
const fixedStars = [
    { name: 'Difta', longitude: 2.22 }, { name: 'Algenib', longitude: 8.794 }, { name: 'Alpheratz', longitude: 13.944 },
    { name: 'Zeta Piscium', longitude: 19.513 }, { name: 'Baton Catos', longitude: 21.587 }, { name: 'Al Ferg', longitude: 26.454 },
    { name: 'Vertex 31M Andromedi', longitude: 27.470 }, { name: 'Mirach', longitude: 30.037 }, { name: 'Sharatan', longitude: 33.606 },
    { name: 'Hamel', longitude: 37.292 }, { name: 'Almac', longitude: 43.87 }, { name: 'Menchar', longitude: 43.959 },
    { name: 'Capulus', longitude: 53.833 }, { name: 'Algol', longitude: 55.805 }, { name: 'Alceyon', longitude: 59.629 },
    { name: 'Aldebaran', longitude: 69.426 }, { name: 'Regel', longitude: 76.466 }, { name: 'Belatrix', longitude: 80.583 },
    { name: 'Capela', longitude: 81.494 }, { name: 'Fact', longitude: 81.804 }, { name: 'Mintaca', longitude: 82.020 },
    { name: 'El Nath', longitude: 82.212 }, { name: 'Ensis', longitude: 82.670 }, { name: 'Alnilam', longitude: 83.102 },
    { name: 'Mrigshira', longitude: 83.344 }, { name: 'El Heca', longitude: 84.42 }, { name: 'Polaris', longitude: 88.204 },
    { name: 'Bittleguice', longitude: 88.391 }, { name: 'Mencalinam', longitude: 89.553 }, { name: 'Tejat', longitude: 93.07 },
    { name: 'Dirac', longitude: 94.937 }, { name: 'Alhina', longitude: 98.737 }, { name: 'Sirius', longitude: 103.723 },
    { name: 'Canopus', longitude: 104.604 }, { name: 'Vasat', longitude: 108.154 }, { name: 'Propus', longitude: 108.587 },
    { name: 'Castor', longitude: 109.878 }, { name: 'Polux', longitude: 112.857 }, { name: 'Proceyon', longitude: 115.427 },
    { name: 'Precipie', longitude: 126.870 }, { name: 'North Ascelis', longitude: 127.17 }, { name: 'South Ascelis', longitude: 128.358 },
    { name: 'Acubens', longitude: 133.27 }, { name: 'Epsilon Hydri', longitude: 131.983 }, { name: 'Alpha Carini', longitude: 104.167 },
    { name: 'Alpha Ursa Majories', longitude: 134.832 }, { name: 'Algenuebi', longitude: 140.337 }, { name: 'Alphard', longitude: 146.92 },
    { name: 'Adphira', longitude: 147.204 }, { name: 'Aljba', longitude: 147.537 }, { name: 'Regulus', longitude: 149.473 },
    { name: 'Zosma', longitude: 160.96 }, { name: 'Denibola', longitude: 171.256 }, { name: 'Copula', longitude: 174.72 },
    { name: 'Labrum', longitude: 176.337 }, { name: 'Zavizva', longitude: 176.787 }, { name: 'Marcab', longitude: 178.554 },
    { name: 'Zania', longitude: 184.47 }, { name: 'Vindemiatrix', longitude: 189.737 }, { name: 'Caffir', longitude: 189.787 },
    { name: 'Algorab', longitude: 193.09 }, { name: 'Seginus', longitude: 197.287 }, { name: 'Foremen', longitude: 201.804 },
    { name: 'Spica', longitude: 203.478 }, { name: 'Arcturus', longitude: 203.87 }, { name: 'Princeps', longitude: 212.787 },
    { name: 'Khambalia', longitude: 216.504 }, { name: 'Acrux', longitude: 221.52 }, { name: 'Alphixa', longitude: 221.92 },
    { name: 'South Scale', longitude: 224.72 }, { name: 'North Scale', longitude: 229.004 }, { name: 'Unukalei', longitude: 231.687 },
    { name: 'Agina', longitude: 233.47 }, { name: 'Bangula', longitude: 239.22 }, { name: 'Yed Prior', longitude: 241.937 },
    { name: 'Isidis', longitude: 242.208 }, { name: 'Graphius', longitude: 242.82 }, { name: 'Han', longitude: 248.87 },
    { name: 'Antares', longitude: 249.399 }, { name: 'Rastaban', longitude: 251.587 }, { name: 'Sabic', longitude: 257.604 },
    { name: 'Russelhague', longitude: 258.087 }, { name: 'New Scorpi', longitude: 263.654 }, { name: 'Lambda Scorpi', longitude: 264.222 },
    { name: 'Aculius', longitude: 265.404 }, { name: 'Acumen', longitude: 268.337 }, { name: 'Sinistra', longitude: 269.387 },
    { name: 'Speculum', longitude: 270.287 }, { name: 'Polis', longitude: 272.854 }, { name: 'Facis', longitude: 277.954 },
    { name: 'Delta Sagitari', longitude: 274.222 }, { name: 'Pegasus', longitude: 282.022 }, { name: 'Asela', longitude: 283.27 },
    { name: 'Manubrium', longitude: 284.62 }, { name: 'Vega', longitude: 284.96 }, { name: 'Deneb', longitude: 289.437 },
    { name: 'Terebellum', longitude: 295.47 }, { name: 'Albirio', longitude: 300.904 }, { name: 'Altair', longitude: 301.409 },
    { name: 'Geidi', longitude: 303.454 }, { name: 'Debi', longitude: 303.687 }, { name: 'Occulus', longitude: 304.254 },
    { name: 'Boss', longitude: 304.804 }, { name: 'Armus', longitude: 312.37 }, { name: 'Dormus', longitude: 313.47 },
    { name: 'Castra', longitude: 319.837 }, { name: 'Nashira', longitude: 321.42 }, { name: 'Saddlesood', longitude: 323.037 },
    { name: 'Deneb Aljedi', longitude: 323.17 }, { name: 'Saddlemeric', longitude: 332.987 }, { name: 'Fomalhaut', longitude: 333.487 },
    { name: 'Deneb Adies', longitude: 334.987 }, { name: 'Scott', longitude: 338.504 }, { name: 'Akernar', longitude: 344.92 },
    { name: 'Marcab', longitude: 353.12 }, { name: 'Skeat', longitude: 359.004 }, { name: 'Delphini Alpha', longitude: 317.033 },
    { name: 'Delphini Beta', longitude: 315.978 }, { name: 'Lambda Aquiri', longitude: 341.212 }
];
const nakshatras = [
    'Ashwini', 'Bharani', 'Krittika', 'Rohini', 'Mrigashira', 'Ardra', 'Punarvasu', 'Pushya', 'Ashlesha', 
    'Magha', 'Purva Phalguni', 'Uttara Phalguni', 'Hasta', 'Chitra', 'Swati', 'Vishakha', 'Anuradha', 
    'Jyeshtha', 'Mula', 'Purva Ashadha', 'Uttara Ashadha', 'Shravana', 'Dhanishta', 'Shatabhisha', 
    'Purva Bhadrapada', 'Uttara Bhadrapada', 'Revati'
];

const TimeAndEventStrategyDialog = ({ open, onClose, onRun }) => {
    const [colors, setColors] = useState({
        harmonic: '#E67C73',
        planet_entering_sign: '#4285F4',
        entering_nakshatra: '#7986CB',
        aspect: '#34A853',
        declination_aspect: '#F4B400',
        zero_declination: '#8E67FD',
        friendly_sign: '#616161',
        inimical_sign: '#FF8A65',
        galactic_center: '#9E69AF',
        rising: '#00ACC1',
        declination_more_than_sun: '#F06292',
        solarOrLunarEclipse: '#A1887F',
        conjunct_star: '#B39DDB'
    });
    const [pickerAnchorEl, setPickerAnchorEl] = useState(null);
    const [currentPicker, setCurrentPicker] = useState(null);
    const colorsRef = useRef(colors);

    const handleColorClick = (event, studyType) => {
        setPickerAnchorEl(event.currentTarget);
        setCurrentPicker(studyType);
    };

    const handleColorClose = () => {
        setPickerAnchorEl(null);
        setCurrentPicker(null);
    };

    const handleColorChange = (color) => {
        if (currentPicker) {
            setColors(prevColors => ({
                ...prevColors,
                [currentPicker]: color.hex
            }));
            colorsRef.current[currentPicker] = color.hex;
        }
    };

    const isPickerOpen = Boolean(pickerAnchorEl);
    const [timeOption, setTimeOption] = useState('before');
    const [highlightedRow, setHighlightedRow] = useState(null);

    useEffect(() => {
        colorsRef.current = colors;
    }, [colors]);

    const [formValues, setFormValues] = useState({
        time_before: '10',
        time_after: '10',
        time_around_before: '10',
        time_around_after: '10',
        harmonic: '12',
        location: '0',
        harmonic_planet: 'Sun',
        entering_sign_planet: 'Mercury',
        entering_nakshatra_planet: 'Moon',
        aspect_planet1: 'Mars',
        aspect_planet2: 'Sun',
        entering_sign: 'Aries',
        entering_nakshatra: 'Ashwini',
        aspect: 0,
        declination_aspect: 'Parallel',
        declination_planet1: 'Mars',
        declination_planet2: 'Mercury',
        zero_declination_planet: 'Mercury',
        friendly_sign_planet: '',
        inimical_sign_planet: '',
        galactic_center_planet: 'Venus',
        rising_planet: '',
        setting_planet: 'Mars',
        rising_value: '',
        eclipse_type: 'Solar',
        conjunct_star_planet: 'Sun',
        conjunct_star: 'Hamel'
    });

    const handleRunStudy = (studyType) => {
        const studyParams = {};
        switch (studyType) {
            case 'harmonic':
                studyParams.planet = formValues.harmonic_planet;
                studyParams.harmonic = formValues.harmonic;
                studyParams.location = formValues.location;
                break;
            case 'planet_entering_sign':
                studyParams.planet = formValues.entering_sign_planet;
                studyParams.sign = signs.indexOf(formValues.entering_sign) + 1;
                break;
            case 'entering_nakshatra':
                studyParams.planet = formValues.entering_nakshatra_planet;
                studyParams.nakshatra = nakshatras.indexOf(formValues.entering_nakshatra) + 1;
                break;
            case 'aspect':
                studyParams.planet1 = formValues.aspect_planet1;
                studyParams.planet2 = formValues.aspect_planet2;
                studyParams.aspect = formValues.aspect;
                break;
            case 'declination_aspect':
                studyParams.planet1 = formValues.declination_planet1;
                studyParams.planet2 = formValues.declination_planet2;
                studyParams.declination_aspect = declinationAspectMap[formValues.declination_aspect];
                break;
            case 'zero_declination':
                studyParams.planet = formValues.zero_declination_planet;
                break;
            case 'friendly_sign':
                studyParams.planet = formValues.friendly_sign_planet;
                break;
            case 'inimical_sign':
                studyParams.planet = formValues.inimical_sign_planet;
                break;
            case 'galactic_center':
                studyParams.planet = formValues.galactic_center_planet;
                break;
            case 'rising':
                studyParams.planet = formValues.rising_planet;
                break;
            case 'declination_more_than_sun':
                studyParams.planet = formValues.setting_planet; // Note: state is setting_planet
                break;
            case 'solarOrLunarEclipse':
                studyParams.type = formValues.eclipse_type;
                break;
            case 'conjunct_star': {
                studyParams.planet = formValues.conjunct_star_planet;
                const selectedStar = fixedStars.find(s => s.name === formValues.conjunct_star);
                if (selectedStar) {
                    studyParams.star_longitude = selectedStar.longitude;
                }
                break;
            }
            default:
                console.error(`Unknown study type: ${studyType}`);
                return;
        }

        const payload = {
            studyType,
            timeOption,
            daysBefore: timeOption === 'before' ? formValues.time_before : (timeOption === 'around' ? formValues.time_around_before : 0),
            daysAfter: timeOption === 'after' ? formValues.time_after : (timeOption === 'around' ? formValues.time_around_after : 0),
            params: {
                ...studyParams,
                color: colorsRef.current[studyType]
            }
        };

        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) {
            alert('You are not logged in. Please log in again.');
            return;
        }

        console.log('Final payload being sent:', JSON.stringify(payload, null, 2));
        setHighlightedRow(studyType);
        onRun(payload);
        user.getIdToken(true).catch(error => {
            console.error('Error refreshing token:', error);
            alert('Could not refresh authentication token. Please log in again.');
        });
    };

    const renderRow = (controls, rowId, disabled) => {
        let isTimeInputInvalid = false;
        if (timeOption === 'before' && !formValues.time_before) {
            isTimeInputInvalid = true;
        } else if (timeOption === 'after' && !formValues.time_after) {
            isTimeInputInvalid = true;
        } else if (timeOption === 'around' && (!formValues.time_around_before || !formValues.time_around_after)) {
            isTimeInputInvalid = true;
        }
        const isHighlighted = highlightedRow === rowId;
        return (
            <Box sx={{
                border: isHighlighted ? '2px solid #1976d2' : '1px solid #ccc',
                borderRadius: '4px',
                p: 1,
                mb: 1,
                backgroundColor: isHighlighted ? '#e3f2fd' : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {controls.map((control, index) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                            {control.component}
                        </Box>
                    ))}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                        sx={{
                            width: 24,
                            height: 24,
                            backgroundColor: colors[rowId],
                            cursor: 'pointer',
                            border: '1px solid #ccc',
                            borderRadius: '4px'
                        }}
                        onClick={(e) => handleColorClick(e, rowId)}
                    />
                    <Button variant="contained" onClick={() => handleRunStudy(rowId)} disabled={disabled || isTimeInputInvalid}>RUN</Button>
                </Box>
            </Box>
        );
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
            <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{flexGrow: 1, textAlign: 'center', paddingLeft: '48px'}}>Time And Event Strategy</span>
        <HelpIcon helpKey="timeAndEventStrategyDialog" />
      </DialogTitle>
            <DialogContent>
                {/* Time Window Selection */}
                <Box sx={{ border: '2px solid #999', borderRadius: '4px', p: 1, my: 1 }}>
                    <RadioGroup row value={timeOption} onChange={(e) => setTimeOption(e.target.value)} sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <FormControlLabel value="before" control={<Radio />} label="From" />
                            <TextField type="number" size="small" sx={{ width: 80, mx: 1 }} value={formValues.time_before} onChange={(e) => setFormValues(v => ({...v, time_before: e.target.value}))} />
                            <Typography sx={{ fontSize: '0.875rem' }}>days before to the event</Typography>
                        </Box>

                        <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(0, 0, 0, 0.4)' }} />

                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <FormControlLabel value="after" control={<Radio />} label="From event to" />
                            <TextField type="number" size="small" sx={{ width: 80, mx: 1 }} value={formValues.time_after} onChange={(e) => setFormValues(v => ({...v, time_after: e.target.value}))} />
                            <Typography sx={{ fontSize: '0.875rem' }}>days after</Typography>
                        </Box>

                        <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(0, 0, 0, 0.4)' }} />

                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <FormControlLabel value="around" control={<Radio />} label="From" />
                            <TextField type="number" size="small" sx={{ width: 80, mx: 1 }} value={formValues.time_around_before} onChange={(e) => setFormValues(v => ({...v, time_around_before: e.target.value}))} />
                            <Typography sx={{ mr: 1, fontSize: '0.875rem' }}>days before to</Typography>
                            <TextField type="number" size="small" sx={{ width: 80, mx: 1 }} value={formValues.time_around_after} onChange={(e) => setFormValues(v => ({...v, time_around_after: e.target.value}))} />
                            <Typography sx={{ fontSize: '0.875rem' }}>days after</Typography>
                        </Box>
                    </RadioGroup>
                </Box>

                {/* Event Rows */}
                <Box sx={{ maxWidth: '55%', margin: '0 auto' }}>
                    {renderRow([
                        { component: <Typography sx={{ fontSize: '0.875rem' }}>Planet</Typography> },
                        { component: (
                            <FormControl size="small" sx={{ width: 120 }}>
                                <Select value={formValues.harmonic_planet} onChange={(e) => setFormValues(v => ({...v, harmonic_planet: e.target.value}))}>
                                    {planets.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                                </Select>
                            </FormControl>
                        )},
                        { component: <Typography sx={{ fontSize: '0.875rem' }}>Harmonic</Typography> },
                        { component: <TextField 
                            type="number" 
                            size="small" 
                            sx={{ width: 67 }} 
                            value={formValues.harmonic} 
                            onChange={(e) => {
                                const newHarmonic = e.target.value;
                                setFormValues(v => ({...v, harmonic: newHarmonic}));

                                const harmonicValue = parseFloat(newHarmonic);
                                if (harmonicValue >= 1 && harmonicValue <= 180) {
                                    const maxLocation = (360 / harmonicValue) - 1;
                                    let locationValue = parseFloat(formValues.location);
                                    if (!isNaN(locationValue) && locationValue > maxLocation) {
                                        setFormValues(v => ({...v, harmonic: newHarmonic, location: maxLocation.toFixed(2)}));
                                    }
                                }
                            }} 
                            /> 
                        },
                        { component: <Typography sx={{ fontSize: '0.875rem' }}>Location in harmonic window in degrees</Typography> },
                        { component: <TextField 
                            type="number" 
                            size="small" 
                            sx={{ width: 67 }} 
                            value={formValues.location} 
                            onChange={(e) => {
                                const newLocation = e.target.value;
                                const harmonic = parseFloat(formValues.harmonic);
                                const locationValue = parseFloat(newLocation);

                                if (newLocation === '' || isNaN(locationValue)) {
                                    setFormValues(v => ({...v, location: newLocation}));
                                    return;
                                }

                                if (harmonic >= 1 && harmonic <= 180) {
                                    const maxLocation = (360 / harmonic) - 1;
                                    if (locationValue > maxLocation) {
                                        setFormValues(v => ({...v, location: maxLocation.toFixed(2)}));
                                    } else if (locationValue < 0) {
                                        setFormValues(v => ({...v, location: '0'}));
                                    } else {
                                        setFormValues(v => ({...v, location: newLocation}));
                                    }
                                } else {
                                    setFormValues(v => ({...v, location: newLocation}));
                                }
                            }}
                            /> 
                        }
                    ], 'harmonic', !formValues.harmonic || !formValues.location)}

                {renderRow([
                    { component: <Typography sx={{ fontSize: '0.875rem' }}>Planet</Typography> },
                    { component: (
                        <FormControl size="small" sx={{ width: 120 }}>
                            <Select value={formValues.entering_sign_planet} onChange={(e) => setFormValues(v => ({...v, entering_sign_planet: e.target.value}))}>
                                {planets.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                            </Select>
                        </FormControl>
                    )},
                    { component: <Typography sx={{ fontSize: '0.875rem' }}>Entering Sign</Typography> },
                    { component: (
                        <FormControl size="small" sx={{ width: 120 }}>
                            <Select value={formValues.entering_sign} onChange={(e) => setFormValues(v => ({...v, entering_sign: e.target.value}))}>
                                {signs.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                            </Select>
                        </FormControl>
                    )}
                ], 'planet_entering_sign', false)}

                {renderRow([
                    { component: <Typography sx={{ fontSize: '0.875rem' }}>Planet</Typography> },
                    { component: (
                        <FormControl size="small" sx={{ width: 120 }}>
                            <Select value={formValues.entering_nakshatra_planet} onChange={(e) => setFormValues(v => ({...v, entering_nakshatra_planet: e.target.value}))}>
                                {planets.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                            </Select>
                        </FormControl>
                    )},
                    { component: <Typography sx={{ fontSize: '0.875rem' }}>Entering Nakshatra</Typography> },
                    { component: (
                        <FormControl size="small" sx={{ width: 120 }}>
                            <Select value={formValues.entering_nakshatra} onChange={(e) => setFormValues(v => ({...v, entering_nakshatra: e.target.value}))}>
                                {nakshatras.map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                            </Select>
                        </FormControl>
                    )}
                ], 'entering_nakshatra')}

                {renderRow([
                    { component: <Typography sx={{ fontSize: '0.875rem' }}>Planet</Typography> },
                    { component: (
                        <FormControl size="small" sx={{ width: 120 }}>
                            <Select value={formValues.aspect_planet1} onChange={(e) => setFormValues(v => ({...v, aspect_planet1: e.target.value}))}>
                                {planets.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                            </Select>
                        </FormControl>
                    )},
                    { component: <Typography sx={{ fontSize: '0.875rem' }}>Planet</Typography> },
                    { component: (
                        <FormControl size="small" sx={{ width: 120 }}>
                            <Select value={formValues.aspect_planet2} onChange={(e) => setFormValues(v => ({...v, aspect_planet2: e.target.value}))}>
                                {planets.filter(p => p !== formValues.aspect_planet1).map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                            </Select>
                        </FormControl>
                    )},
                    { component: <Typography sx={{ fontSize: '0.875rem' }}>Aspect degrees</Typography> },
                    { component: (
                        <TextField 
                            type="number" 
                            size="small" 
                            sx={{ width: 80 }} 
                            value={formValues.aspect} 
                            onChange={(e) => {
                                const val = e.target.value;
                                if (val === '' || (val >= 0 && val <= 359)) {
                                    setFormValues(v => ({...v, aspect: val}));
                                }
                            }}
                            inputProps={{ min: 0, max: 359 }}
                        />
                    )}
                ], 'aspect', formValues.aspect_planet1 === formValues.aspect_planet2)}

                {renderRow([
                    { component: <Typography sx={{ fontSize: '0.875rem' }}>Planet</Typography> },
                    { component: (
                        <FormControl size="small" sx={{ width: 120 }}>
                            <Select value={formValues.declination_planet1} onChange={(e) => setFormValues(v => ({...v, declination_planet1: e.target.value}))}>
                                {planets.filter(p => !['Moon', 'Rahu', 'Ketu'].includes(p) && p !== 'Sun').map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                                <MenuItem key="Sun" value="Sun">Sun</MenuItem>
                            </Select>
                        </FormControl>
                    )},
                    { component: <Typography sx={{ fontSize: '0.875rem' }}>Planet</Typography> },
                    { component: (
                        <FormControl size="small" sx={{ width: 120 }}>
                            <Select value={formValues.declination_planet2} onChange={(e) => setFormValues(v => ({...v, declination_planet2: e.target.value}))}>
                                {planets.filter(p => !['Moon', 'Rahu', 'Ketu'].includes(p) && p !== formValues.declination_planet1 && p !== 'Sun').map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                                <MenuItem key="Sun" value="Sun">Sun</MenuItem>
                            </Select>
                        </FormControl>
                    )},
                    { component: <Typography sx={{ fontSize: '0.875rem' }}>Declination Aspect</Typography> },
                    { component: (
                        <FormControl size="small" sx={{ width: 120 }}>
                            <Select value={formValues.declination_aspect} onChange={(e) => setFormValues(v => ({...v, declination_aspect: e.target.value}))}>
                                {declinationAspects.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                            </Select>
                        </FormControl>
                    )}
                ], 'declination_aspect', formValues.declination_planet1 === formValues.declination_planet2 || !formValues.declination_aspect)}

                {renderRow([
                    { component: <Typography sx={{ fontSize: '0.875rem' }}>Planet</Typography> },
                    { component: (
                        <FormControl size="small" sx={{ width: 120 }}>
                            <Select value={formValues.zero_declination_planet} onChange={(e) => setFormValues(v => ({...v, zero_declination_planet: e.target.value}))}>
                                {planets.filter(p => !['Moon', 'Rahu', 'Ketu'].includes(p)).map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                            </Select>
                        </FormControl>
                    )},
                    { component: <Typography sx={{ fontSize: '0.875rem' }}>Zero Declination</Typography> }
                ], 'zero_declination')}

                {renderRow([
                    { component: <Typography sx={{ fontSize: '0.875rem' }}>Planet</Typography> },
                    { component: (
                        <FormControl size="small" sx={{ width: 120 }} disabled>
                            <Select value={formValues.friendly_sign_planet}>
                                {planets.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                            </Select>
                        </FormControl>
                    )},
                    { component: <Typography sx={{ fontSize: '0.875rem' }}></Typography> }
                ], 'friendly_sign', true)}

                {renderRow([
                    { component: <Typography sx={{ fontSize: '0.875rem' }}>Planet</Typography> },
                    { component: (
                        <FormControl size="small" sx={{ width: 120 }} disabled>
                            <Select value={formValues.inimical_sign_planet}>
                                {planets.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                            </Select>
                        </FormControl>
                    )},
                    { component: <Typography sx={{ fontSize: '0.875rem' }}></Typography> }
                ], 'inimical_sign', true)}

                {renderRow([
                    { component: <Typography sx={{ fontSize: '0.875rem' }}>Planet</Typography> },
                    { component: (
                        <FormControl size="small" sx={{ width: 120 }} disabled>
                            <Select value={formValues.rising_planet} onChange={(e) => setFormValues(v => ({...v, rising_planet: e.target.value}))}>
                                {planets.filter(p => p !== 'Sun' && p !== 'Moon').map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                            </Select>
                        </FormControl>
                    )},
                    { component: <Typography sx={{ fontSize: '0.875rem' }}></Typography> }
                ], 'rising', true)}

                {renderRow([
                    { component: <Typography sx={{ fontSize: '0.875rem' }}>Planet</Typography> },
                    { component: (
                        <FormControl size="small" sx={{ width: 120 }}>
                            <Select value={formValues.setting_planet} onChange={(e) => setFormValues(v => ({...v, setting_planet: e.target.value}))}>
                                {planets.filter(p => p !== 'Sun').map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                            </Select>
                        </FormControl>
                    )},
                    { component: <Typography sx={{ fontSize: '0.875rem' }}>Declination more than that of the Sun</Typography> }
                ], 'declination_more_than_sun')}

                {renderRow([
                    { component: <Typography sx={{ fontSize: '0.875rem' }}>Eclipse</Typography> },
                    { component: (
                        <FormControl size="small" sx={{ width: 120 }}>
                            <Select value={formValues.eclipse_type} onChange={(e) => setFormValues(v => ({...v, eclipse_type: e.target.value}))}>
                                <MenuItem value="Solar">Solar</MenuItem>
                                <MenuItem value="Lunar">Lunar</MenuItem>
                            </Select>
                        </FormControl>
                    )}
                ], 'solarOrLunarEclipse', false)}

                {renderRow([
                    { component: <Typography sx={{ fontSize: '0.875rem' }}>Planet</Typography> },
                    { component: (
                        <FormControl size="small" sx={{ width: 120 }}>
                            <Select value={formValues.conjunct_star_planet} onChange={(e) => setFormValues(v => ({...v, conjunct_star_planet: e.target.value}))}>
                                {planets.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                            </Select>
                        </FormControl>
                    )},
                    { component: <Typography sx={{ fontSize: '0.875rem' }}>Conjunct with Star</Typography> },
                    { component: (
                        <FormControl size="small" sx={{ width: 120 }}>
                            <Select value={formValues.conjunct_star} onChange={(e) => setFormValues(v => ({...v, conjunct_star: e.target.value}))}>
                                {fixedStars.map(s => <MenuItem key={s.name} value={s.name}>{s.name}</MenuItem>)}
                            </Select>
                        </FormControl>
                    )}
                ], 'conjunct_star')}
                </Box>

                <Popover
                    open={isPickerOpen}
                    anchorEl={pickerAnchorEl}
                    onClose={handleColorClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'center',
                    }}
                >
                    <SketchPicker
                        color={colors[currentPicker]}
                        onChangeComplete={handleColorChange}
                    />
                </Popover>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'flex-start' }}>
                <Button onClick={onClose} variant="contained" color="error">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

TimeAndEventStrategyDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onRun: PropTypes.func.isRequired,
};

export default TimeAndEventStrategyDialog;